import { createContext, type ReactNode } from 'react'
import cx from 'classnames'
import { type AvatarSubject, Avatar } from '@/components/Avatar'
import { useIsElementInView } from '@/hooks/element-hooks'
import { truncate } from 'lodash-es'
import styles from './MessageGroup.module.css'
import {
  EditableMessageContent,
  type EditableMessageContentProps,
} from '@/features/messaging/components/ConversationView/Messages/List/EditableMessageContent'
import { dateFormatters } from '@/utils/date-util'

/**
 * Set by hosts narrow enough to read the conversation as chat bubbles.
 */
export const BubbleLayoutContext = createContext(false)

export interface MessageSender {
  avatar: AvatarSubject
  online: boolean
  isCurrentUser: boolean
  displayName: string
}

export type MessageEditProps = Omit<EditableMessageContentProps, 'children'>

export interface MessageGroupProps {
  showDateLine: boolean
  timestamp: Date
  children?: ReactNode
}

export function MessageGroup({
  showDateLine,
  timestamp,
  children,
}: MessageGroupProps) {
  return (
    <div>
      {showDateLine && (
        <div className={styles.dateline}>
          <div className={styles.datelineStroke} />
          <div className={styles.datelineValue}>
            {dateFormatters.dateWeekday.format(timestamp)}
          </div>
          <div className={styles.datelineStroke} />
        </div>
      )}
      <div className={styles.messages}>{children}</div>
    </div>
  )
}

export interface MessageGroupMessageProps {
  first: boolean
  isNote: boolean
  sender: MessageSender
  tagLabel: string | null
  groupTimestamp: Date
  dateCreated: Date
  onInView: () => unknown
  edit: MessageEditProps
  content: ReactNode
}

export function MessageGroupMessage({
  first,
  isNote,
  sender,
  tagLabel,
  groupTimestamp,
  dateCreated,
  onInView,
  edit,
  content,
}: MessageGroupMessageProps) {
  const { connectRef: messageRef } = useIsElementInView((isInView) => {
    if (isInView) {
      onInView()
    }
  })

  return (
    <div
      ref={messageRef}
      className={cx(
        styles.message,
        isNote && styles.isNote,
        !edit.isEditing && styles.messageHighlight,
      )}
    >
      <div className={styles.gutter}>
        {first ? (
          <div className={styles.avatarContainer}>
            <Avatar
              size={38}
              subject={sender.avatar}
              badge={sender.online ? 'online' : 'offline'}
            />
          </div>
        ) : (
          <div className={styles.secondaryTimestamp}>
            {dateFormatters.timeDefault.format(dateCreated)}
          </div>
        )}
      </div>

      <EditableMessageContent {...edit}>
        <div className={styles.content}>
          {first ? (
            <div className={styles.header}>
              <p>
                <span
                  className={cx(
                    styles.displayName,
                    sender.isCurrentUser && styles.me,
                  )}
                >
                  {truncate(sender.displayName, { length: 100 })}
                </span>
                {tagLabel && (
                  <>
                    {' '}
                    <span className={styles.groupTag}>{tagLabel}</span>
                  </>
                )}{' '}
                <span className={styles.timestamp}>
                  {dateFormatters.timeDefault.format(groupTimestamp)}
                </span>
              </p>
            </div>
          ) : null}
          {content}
        </div>
      </EditableMessageContent>
      <div className={styles.extra}>
        {isNote && <div className={styles.messageTag}>PERSONAL NOTE</div>}
      </div>
    </div>
  )
}
